import axios from 'axios'

import { getApiV1BaseUrl } from '@/constants'
import { ApiCountry, ApiLocation, ApiRegion } from '@/types/api/countries'
import { LocationOption } from '@/types/Location'
import { flow, types } from 'mobx-state-tree'

export const initialRegionState = {
  loading: false,
  error: false,
  countries: [],
  fetchController: null,
}

export const RegionStore = types
  .model('RegionStore')
  .props({
    loading: types.boolean,
    error: types.boolean,
    regions: types.array(types.frozen<ApiRegion>()),
    countries: types.array(types.frozen<ApiCountry>()),
    internationalCountries: types.array(types.frozen<ApiCountry>()),
    fetchController: types.maybeNull(types.frozen<AbortController>()),
  })
  .views((self) => ({
    get selectOptions() {
      const _flatLocationsInternational: LocationOption[] = []
      const _flatUkLocations: LocationOption[][] = []

      for (const country of self.countries) {
        if (country.name === 'International') {
          for (const region of country.regions) {
            for (const location of region.locations) {
              _flatLocationsInternational.push({
                label: location.name,
                value: location.id,
                type: 'country',
              })
            }
          }

          continue
        }

        for (const region of country.regions) {
          if (!_flatUkLocations[region.display_column]) {
            _flatUkLocations[region.display_column] = []
          }
          _flatUkLocations[region.display_column].push({
            label: region.name,
            value: region.id,
            type: 'region',
          })
          for (const location of region.locations) {
            _flatUkLocations[region.display_column].push({
              label: location.name,
              value: location.id,
              type: 'location',
            })
          }
        }
      }

      return {
        flatLocationsInternational: _flatLocationsInternational,
        flatUkLocations: _flatUkLocations,
      }
    },
    get allCountries() {
      return [...self.countries, ...self.internationalCountries]
    },
    get flatLocations() {
      return self.countries.reduce((acc, country) => {
        const flatLocation = country.regions.reduce((acc, region) => {
          if (region.name !== 'International') {
            acc = [...acc, ...region.locations]
          }
          return acc
        }, [] as ApiLocation[])
        return (acc = [...acc, ...flatLocation])
      }, [] as ApiLocation[])
    },
  }))
  .actions((self) => {
    return {
      cancelFetch: () => {
        if (self.fetchController) {
          self.fetchController.abort()
        }
      },
      fetchLocations: flow(function* () {
        if (self.countries.length > 0) return
        if (self.loading) return

        try {
          self.loading = true
          self.fetchController = new AbortController()
          const { data } = yield axios.get(`${getApiV1BaseUrl()}/regions`, {
            signal: self.fetchController.signal,
          })
          self.countries = data
          self.internationalCountries = data.reduce(
            (acc: any[], current: ApiCountry) => {
              if (
                current.name === 'International' &&
                current.regions[0].locations
              ) {
                acc = [...acc, ...current.regions[0].locations]
              }
              return acc
            },
            [] as any
          )
          self.regions = data.reduce(
            (acc: any[], current: { regions: any; name: string }) => {
              acc = [...acc, ...current.regions]
              return acc
            },
            [] as any
          )
        } catch (error) {
          console.log(`>> fetchLocations: ${error}`)
          self.error = true
        }

        self.loading = false
      }),
    }
  })
